import { fetchAuthSession } from 'aws-amplify/auth';
import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  Restaurant,
  MenuItem,
  DailyLunch,
  LunchOrder,
  LunchSummary,
  User,
  ApiError,
  Country,
} from '../types';

class ApiClient {
  private client: AxiosInstance;
  private apiUrl: string;

  constructor() {
    this.apiUrl = import.meta.env.VITE_API_URL;

    this.client = axios.create({
      baseURL: this.apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      async (config) => {
        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();

          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Error getting auth token:', error);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        if (error.response) {
          const apiError: ApiError = {
            message: error.response.data?.message || 'An error occurred',
            code: error.response.data?.code,
            statusCode: error.response.status,
          };
          return Promise.reject(apiError);
        }

        const networkError = {
          message: error.message || 'Network error',
          code: 'NETWORK_ERROR',
        } as ApiError;
        return Promise.reject(networkError);
      }
    );
  }

  // ==================== Health Check ====================
  async healthCheck(): Promise<{ status: string; service: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // ==================== Restaurants ====================
  async getRestaurants(): Promise<Restaurant[]> {
    const response = await this.client.get('/restaurants');
    return response.data.restaurants;
  }

  async getRestaurant(id: string): Promise<Restaurant> {
    const response = await this.client.get(`/restaurants/${id}`);
    return response.data.restaurant;
  }

  async createRestaurant(data: {
    name: string;
    country: Country;
    description?: string;
    imageUrl?: string;
    whatsappNumber?: string;
    isDefault?: boolean;
  }): Promise<Restaurant> {
    const response = await this.client.post('/admin/restaurants', data);
    return response.data.restaurant;
  }

  async updateRestaurant(
    id: string,
    data: {
      name?: string;
      country?: Country;
      description?: string;
      imageUrl?: string;
      whatsappNumber?: string;
      active?: boolean;
      isDefault?: boolean;
    }
  ): Promise<Restaurant> {
    const response = await this.client.put(`/admin/restaurants/${id}`, data);
    return response.data.restaurant;
  }

  async deleteRestaurant(id: string): Promise<void> {
    await this.client.delete(`/admin/restaurants/${id}`);
  }

  async getAllRestaurants(): Promise<Restaurant[]> {
    const response = await this.client.get('/admin/restaurants');
    return response.data.restaurants;
  }

  // ==================== Menu Items ====================
  async getMenuItems(restaurantId: string): Promise<MenuItem[]> {
    console.log('[API Client] Fetching menu items for restaurant:', restaurantId);
    const response = await this.client.get(`/restaurants/${restaurantId}/menu`);
    console.log('[API Client] Menu items response:', response.data);
    return response.data.menuItems || [];
  }

  async createMenuItem(
    restaurantId: string,
    data: {
      name: string;
      description?: string;
      price?: number;
      category?: string;
      imageUrl?: string;
      isCeliac?: boolean;
    }
  ): Promise<MenuItem> {
    const response = await this.client.post(`/admin/restaurants/${restaurantId}/menu`, data);
    return response.data.menuItem;
  }

  async updateMenuItem(
    restaurantId: string,
    itemId: string,
    data: {
      name?: string;
      description?: string;
      price?: number;
      category?: string;
      imageUrl?: string;
      available?: boolean;
      isCeliac?: boolean;
    }
  ): Promise<MenuItem> {
    const response = await this.client.put(
      `/admin/restaurants/${restaurantId}/menu/${itemId}`,
      data
    );
    return response.data.menuItem;
  }

  async deleteMenuItem(restaurantId: string, itemId: string): Promise<void> {
    await this.client.delete(`/admin/restaurants/${restaurantId}/menu/${itemId}`);
  }

  async getAllMenuItems(restaurantId: string): Promise<MenuItem[]> {
    const response = await this.client.get(`/admin/restaurants/${restaurantId}/menu`);
    return response.data.menuItems;
  }

  // ==================== Lunch ====================
  async getTodaysLunch(): Promise<DailyLunch> {
    const response = await this.client.get('/lunch/today');
    return response.data.lunch;
  }

  async getLunch(date: string): Promise<DailyLunch> {
    const response = await this.client.get(`/lunch/${date}`);
    return response.data.lunch;
  }

  async closeLunch(date: string): Promise<DailyLunch> {
    const response = await this.client.post(`/admin/lunch/${date}/close`);
    return response.data.lunch;
  }

  async closeLunchWithManualEntry(
    date: string,
    data: {
      restaurantName: string;
      totalDiners: number;
      totalAmount: number;
      notes?: string;
    }
  ): Promise<DailyLunch> {
    const response = await this.client.post(`/admin/lunch/${date}/close-manual`, data);
    return response.data.lunch;
  }

  async reopenLunch(date: string): Promise<DailyLunch> {
    const response = await this.client.post(`/admin/lunch/${date}/reopen`);
    return response.data.lunch;
  }

  async archiveLunch(date: string): Promise<DailyLunch> {
    const response = await this.client.post(`/admin/lunch/${date}/archive`);
    return response.data.lunch;
  }

  async getLunchSummary(date: string): Promise<LunchSummary> {
    const response = await this.client.get(`/admin/lunch/${date}`);
    return response.data.summary;
  }

  async getOrdersByDateRange(startDate?: string, endDate?: string, country?: Country): Promise<LunchOrder[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (country) params.append('country', country);
    const response = await this.client.get(`/admin/reports/orders?${params.toString()}`);
    return response.data.orders;
  }

  // ==================== Orders ====================
  async getMyOrders(): Promise<LunchOrder[]> {
    const response = await this.client.get('/orders/my');
    return response.data.orders;
  }

  async getMyOrder(date: string): Promise<LunchOrder | null> {
    const response = await this.client.get(`/orders/my/${date}`);
    return response.data.order;
  }

  async createOrder(data: {
    date: string;
    restaurantId: string;
    menuItemId: string;
  }): Promise<LunchOrder> {
    const response = await this.client.post('/orders', data);
    return response.data.order;
  }

  async updateOrder(
    date: string,
    data: {
      restaurantId: string;
      menuItemId: string;
    }
  ): Promise<LunchOrder> {
    const response = await this.client.put(`/orders/${date}`, data);
    return response.data.order;
  }

  async cancelOrder(date: string): Promise<LunchOrder> {
    const response = await this.client.delete(`/orders/${date}`);
    return response.data.order;
  }

  async adminCancelOrder(date: string, userId: string): Promise<LunchOrder> {
    const response = await this.client.delete(`/admin/orders/${date}/${userId}`);
    return response.data.order;
  }

  // ==================== Users (Admin) ====================
  async getUsers(): Promise<User[]> {
    const response = await this.client.get('/admin/users');
    return response.data.users || [];
  }

  async createUser(data: {
    email: string;
    name: string;
    country: Country;
  }): Promise<{ user: User; temporaryPassword: string }> {
    const response = await this.client.post('/admin/users', data);
    return {
      user: response.data.user,
      temporaryPassword: response.data.temporaryPassword
    };
  }

  async updateUserRole(email: string, role: 'ADMIN' | 'USER', country: Country): Promise<void> {
    await this.client.put(`/admin/users/${encodeURIComponent(email)}/role`, { role, country });
  }

  async updateUserCountry(email: string, country: Country): Promise<void> {
    await this.client.put(`/admin/users/${encodeURIComponent(email)}/country`, { country });
  }

  async resetUserPassword(email: string): Promise<string> {
    const response = await this.client.post(`/admin/users/${encodeURIComponent(email)}/reset-password`);
    return response.data.temporaryPassword;
  }

  async disableUser(email: string): Promise<void> {
    await this.client.post(`/admin/users/${encodeURIComponent(email)}/disable`);
  }

  async enableUser(email: string): Promise<void> {
    await this.client.post(`/admin/users/${encodeURIComponent(email)}/enable`);
  }

  async deleteUser(email: string): Promise<void> {
    await this.client.delete(`/admin/users/${encodeURIComponent(email)}`);
  }
}

export const apiClient = new ApiClient();
